import { createContext, useContext, type ComponentType } from "react";
import { Alert } from "@cloudscape-design/components";
import type { Widget } from "./viewContract";
import { pluralNameFor, type SchemaContentType } from "./schemaApi";
import TableWidgetView from "./TableWidgetView";
import TabsWidgetView from "./TabsWidgetView";
import DetailWidgetView from "./DetailWidgetView";

/**
 * Widget render pipeline — the documented 4-stage order:
 *
 *   (1) DynamicPage loads the page by slug and parseView()s the view -> widgets[]
 *       (STRUCTURAL validation only; no data is fetched here).
 *   (2) DynamicPage loads the page-schema ONCE (fetchContentTypes) and provides it to
 *       every widget via WidgetRenderContext (context), together with schema-derived
 *       helpers (e.g. pluralNameFor).
 *   (3) DynamicPage maps widgets[] IN ORDER to registry COMPONENTS with STABLE keys
 *       (`${type}-${index}`), rendering each as a real JSX element `<Comp .../>` (NOT a
 *       bare function call). Stable element identity preserves focus/caret across
 *       re-renders — the lesson from the earlier filter-input focus-loss fix.
 *   (4) each widget COMPONENT runs its OWN data lifecycle with SCOPED loading/error
 *       state, so a failing widget shows its own Alert without breaking its siblings.
 *
 * DynamicPage is therefore a THIN HOST: load page record + parse + load schema +
 * render ordered widgets through this registry. It fetches NO collection data itself;
 * each table widget fetches its own.
 */

/**
 * Shared context handed to every widget. Carries the loaded schema and schema-derived
 * helpers. Each widget OWNS its own data (fetched inside the component), so no data is
 * carried here.
 */
export interface WidgetRenderContextValue {
  /** The full introspected content-type schema, loaded once by DynamicPage. */
  schema: SchemaContentType[];
  /** Resolve a content-type uid to its REST plural endpoint name (or null). */
  pluralNameFor: (uid: string) => string | null;
  /**
   * The CURRENTLY PRESENT URL query params (name -> value), read ONCE by DynamicPage.
   * Widgets with param-bound filters read values from this map; they do NOT parse the URL
   * themselves. Updated when the URL query string changes so dependent widgets refetch.
   */
  urlParams: Record<string, string>;
}

const WidgetRenderContext = createContext<WidgetRenderContextValue | null>(null);

/** Provider mounted once by DynamicPage around the whole widget tree. */
export function WidgetRenderProvider({
  schema,
  urlParams,
  children,
}: {
  schema: SchemaContentType[];
  urlParams: Record<string, string>;
  children: React.ReactNode;
}) {
  const value: WidgetRenderContextValue = {
    schema,
    pluralNameFor: (uid: string) => pluralNameFor(schema, uid),
    urlParams,
  };
  return <WidgetRenderContext.Provider value={value}>{children}</WidgetRenderContext.Provider>;
}

/** Hook used inside widget components to read the shared schema/helpers. */
export function useWidgetRenderContext(): WidgetRenderContextValue {
  const ctx = useContext(WidgetRenderContext);
  if (!ctx) {
    throw new Error("useWidgetRenderContext must be used within a WidgetRenderProvider.");
  }
  return ctx;
}

/**
 * Every widget component receives its own (not-yet-narrowed) widget node. It narrows on
 * `widget.type` internally and reads shared schema via useWidgetRenderContext().
 */
export interface WidgetComponentProps {
  widget: Widget;
}

/**
 * The widget REGISTRY: maps a widget `type` to a React COMPONENT. DynamicPage (and the
 * recursive TabsWidgetView) render these as `<Comp widget={...} />` with a stable key.
 * An unknown type is absent from this map; the caller renders a scoped Alert instead.
 */
export const WIDGET_REGISTRY: Record<string, ComponentType<WidgetComponentProps>> = {
  table: TableWidgetView,
  tabs: TabsWidgetView,
  detail: DetailWidgetView,
};

/**
 * Render a single widget node through the registry with a stable key, falling back to a
 * scoped Alert for unknown types. Shared by DynamicPage (top-level widgets) and
 * TabsWidgetView (recursive tab content) so unknown-type handling is identical everywhere.
 */
export function renderWidget(widget: Widget, index: number): React.ReactElement {
  const type = (widget as { type: string }).type;
  const Comp = WIDGET_REGISTRY[type];
  if (!Comp) {
    return (
      <Alert key={`${type}-${index}`} type="error" header="Unknown widget">
        Widget type "{type}" is not supported.
      </Alert>
    );
  }
  return <Comp key={`${type}-${index}`} widget={widget} />;
}
